import { useSelector } from 'react-redux';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import { NoPiiTestAsset } from '@core/context/types';
import { selectInput } from '@core/reducers/inputsSlice';
import { selectTestResultById } from '@core/reducers/monarchSlice';

type NoPiiVariant = 'noPiiZipOnly' | 'noPiiZipName' | 'control' | null;

type NoPiiVariantResult = {
  variant: NoPiiVariant;
  isNoPiiTestEnabled: boolean;
  startDateValues: (number | null)[] | null;
  startTime: number | null | undefined;
};

/**
 * Hook to check noPii test variant and whether it's enabled based on startTime
 */
export const useNoPiiVariant = (): NoPiiVariantResult => {
  const flags = useFeatureFlags();
  const noPiiFromMonarch = useSelector((state) => selectTestResultById(state, 'noPii'));
  const noPii = (flags?.noPii || noPiiFromMonarch || false) as NoPiiTestAsset | false;
  const startTime = useSelector((state) => selectInput(state, 'startTime'));

  if (!noPii || typeof noPii !== 'object') {
    return {
      variant: null,
      isNoPiiTestEnabled: false,
      startDateValues: null,
      startTime,
    };
  }

  const variantCandidate = (noPii as { variant?: string }).variant;
  const variant: NoPiiVariant =
    variantCandidate === 'noPiiZipOnly' || variantCandidate === 'noPiiZipName'
      ? (variantCandidate as NoPiiVariant)
      : 'control';
  const { startDateValues = null } = noPii;

  // Normalize startTime and startDateValues to numbers for comparison
  const normalizeValue = (value: number | string | null | undefined) => {
    if (value === undefined) return undefined;
    if (value === null) return null;
    return Number(value);
  };

  const normalizedStartTime = normalizeValue(startTime as any);
  const normalizedStartDates = Array.isArray(startDateValues)
    ? startDateValues.map((value) => normalizeValue(value as any))
    : [];

  // Check if normalizedStartTime matches any of the normalized start dates, including null sentinels
  const isNoPiiTestEnabled = normalizedStartDates.includes(normalizedStartTime);

  return {
    variant,
    isNoPiiTestEnabled,
    startDateValues,
    startTime,
  };
};
